import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { themeService, Theme, ThemeStats, ThemeStatsResponse } from '../services/themeService'
import { useJiraConfiguredQuery } from './useJiraConfig'

/**
 * Hook to fetch all themes (only when Jira is configured)
 */
export const useThemes = (jql?: string) => {
  return useJiraConfiguredQuery(
    ['themes', jql],
    () => themeService.getAllThemes(jql),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      select: (data: any) => {
        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch themes')
        }
        return data.data
      },
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  )
}

/**
 * Hook to fetch theme statistics (only when Jira is configured)
 */
export const useThemeStats = () => {
  return useJiraConfiguredQuery(
    ['theme-stats'],
    () => themeService.getThemeStats(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      select: (data: any) => {
        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch theme statistics')
        }
        return data.data
      },
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  )
}

/**
 * Hook to search themes (only when Jira is configured)
 */
export const useThemeSearch = (searchTerm: string) => {
  return useJiraConfiguredQuery(
    ['themes', 'search', searchTerm],
    () => themeService.searchThemes(searchTerm),
    {
      enabled: !!searchTerm.trim(),
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
      select: (data: any) => {
        if (!data.success) {
          throw new Error(data.error || 'Failed to search themes')
        }
        return data.data
      },
      retry: 1,
    }
  )
}

/**
 * Hook to get enriched themes with statistics
 */
export const useEnrichedThemes = (jql?: string) => {
  const { data: themes, isLoading: themesLoading, error: themesError } = useThemes(jql)
  const { data: stats, isLoading: statsLoading, error: statsError } = useThemeStats()

  const enrichedThemes = React.useMemo(() => {
    if (!themes || !stats) return []

    const themeStatsMap = new Map<string, ThemeStats>()
    stats.themes.forEach(stat => {
      themeStatsMap.set(stat.name, stat)
    })

    return themes.map(theme => ({
      ...theme,
      count: themeStatsMap.get(theme.name)?.total || 0,
      stats: themeStatsMap.get(theme.name)
    }))
  }, [themes, stats])

  return {
    data: enrichedThemes,
    isLoading: themesLoading || statsLoading,
    error: themesError || statsError,
    themes,
    stats
  }
}

/**
 * Hook to get unique theme names sorted alphabetically
 */
export const useThemeNames = (jql?: string) => {
  const { data: themes, isLoading, error } = useThemes(jql)

  const themeNames = React.useMemo(() => {
    if (!themes) return []
    return themeService.getUniqueThemeNames(themes)
  }, [themes])

  return {
    data: themeNames,
    isLoading,
    error
  }
}

// Re-export types for convenience
export type { Theme, ThemeStats, ThemeStatsResponse }
